import * as Yup from "yup";
import i18next from "../../i18n";

/**
 * Values React Hook Form manages for the Basic Information wizard sub-form (Task 8, Carbon
 * migration). `annualRevenue` is kept as a STRING here, not `number` — Carbon's `TextInput` (via
 * the shared `TextField` primitive, `{...field}` spread) always reports its native
 * `ChangeEvent.target.value`, which is a string even for `type="number"` inputs; converting to a
 * real number happens once, at submit time, in `BasicInformationForm.tsx`'s own payload builder
 * (same "the RHF value stays a string; conversion happens at assembly" precedent as
 * `EditSiteForm.tsx`'s `address`).
 */
export interface BasicInformationFormFields {
  name: string;
  commercialName: string;
  taxId: string;
  website: string;
  linkedinUrl: string;
  annualRevenue: string;
}

/**
 * Field-level rules exported individually, same convention as
 * `newMaterialFormValidationSchema.ts`. Every max-length here mirrors the backend's own
 * `CompanyProfileUpdateRequest` bean-validation `@Size` constraints exactly (see that class in
 * `spectum`) — sending a value the backend would 400 on is now caught client-side instead.
 */
const maxLengthMessage = (max: number) =>
  i18next.t("validations.maxLength", {
    max,
    defaultValue: `Must not exceed ${max} characters`,
  });

/**
 * `name`/`commercialName` carry NO `.required()` — reproducing the pre-Carbon form's own
 * behaviour (a plain `<div>`, never wrapped in a `<form>`, so no validation of any kind ever ran)
 * AND matching the backend's own documented contract: `CompanyProfileUpdateRequest`'s docstring
 * states these fields validate FORMAT/SIZE only, "do NOT force optional fields to be present" —
 * `CompanyService#updateCompanyProfile` skips a blank `name`/`commercialName` rather than
 * rejecting it. Only `.max()` is new (see `maxLengthMessage` above).
 */
export const nameValidator = Yup.string().max(255, maxLengthMessage(255));

export const commercialNameValidator = Yup.string().max(
  255,
  maxLengthMessage(255)
);

/**
 * `taxId` is the one field that changes shape, not just gains a length cap. The pre-Carbon form
 * rendered it `disabled` (never user-editable) and enforced nothing — but Task 2 added
 * `@NotBlank` to the backend's `CompanyProfileUpdateRequest.taxId`, and EVERY wizard sub-form
 * (this one included) saves by spreading the full profile (`{...profile, ...changedFields}`) into
 * `PUT /company/{id}/profile` — so an unguarded save from ANY step now 400s if `taxId` is
 * missing. `.required()` here is a client-side mirror of that backend rule, not a new UX
 * restriction: the field stays disabled, so a user can never actually trigger this error by
 * typing — it only protects against `profile.taxId` itself arriving blank (e.g. a malformed
 * fixture), same defensive intent as the backend's own `@NotBlank`. `.trim()` matches Bean
 * Validation's own `@NotBlank`, which treats a whitespace-only string as blank too.
 */
export const taxIdValidator = Yup.string()
  .trim()
  .required(i18next.t("validations.taxId"))
  .max(100, maxLengthMessage(100));

/**
 * `website`/`linkedinUrl` carry no URL-format check, matching BOTH the pre-Carbon form (no
 * validation at all) AND the backend (`@Size(max = 500)` only — no `@URL`/pattern constraint).
 * Adding a `.url()` check here would be a NEW restriction unsupported by either side of this
 * change, and would risk rejecting a save that only touches an unrelated field (e.g. this task's
 * own e2e test edits `commercialName` while `website ""` round-trips unchanged) if an
 * already-stored value doesn't happen to parse as a URL.
 */
export const websiteValidator = Yup.string().max(500, maxLengthMessage(500));

export const linkedinUrlValidator = Yup.string().max(
  500,
  maxLengthMessage(500)
);

/**
 * `annualRevenue` had a `type="number" min="0"` input attribute pre-Carbon, but — same root cause
 * as every other field on this form — it was never inside a `<form>`, so the browser's
 * constraint-validation API never ran and `min="0"` was purely decorative; the state setter even
 * stored the raw string `e.target.value` into a field typed `number` (an unguarded `as any` cast
 * papered over it). This schema makes the decorative intent (a non-negative number) real, and
 * reuses `validations.validNumber`/`validations.positiveNumber` — already-established message
 * keys used identically by the dead-code `companyProfileValidationSchema` in
 * `utils/validations.ts` (never wired to any component; not a competing source of truth). Both
 * checks skip an empty string — this field stays optional, matching the backend's unconstrained
 * `BigDecimal annualRevenue`.
 */
export const annualRevenueValidator = Yup.string()
  .test(
    "annual-revenue-is-number",
    i18next.t("validations.validNumber"),
    (value: string | undefined) => !value || !Number.isNaN(Number(value))
  )
  .test(
    "annual-revenue-non-negative",
    i18next.t("validations.positiveNumber"),
    (value: string | undefined) => !value || Number(value) >= 0
  );

export const basicInformationValidationSchema = Yup.object({
  name: nameValidator,
  commercialName: commercialNameValidator,
  taxId: taxIdValidator,
  website: websiteValidator,
  linkedinUrl: linkedinUrlValidator,
  annualRevenue: annualRevenueValidator,
});
